import React from "react";
import { FormattedMessage } from "react-intl";
import { NavLink } from "react-router-dom";

import { getLastCorrection } from "~/common/patient";
import { PersonName } from "~/components/common/PersonName";
import { Button } from "~/components/ui/button";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { selectServices } from "~/slices/services";
import { useAppSelector } from "~/store";
import type { TPatient } from "~/types/patient";

export function PatientAdditionalAlignersCard({ patient }: { patient: TPatient }) {
  const { can_order_additional_aligners } = getLastCorrection(patient).order_options;
  const services = useAppSelector(selectServices);
  const showAligners = services.some((service) => service.tag == "ADDITIONAL_ALIGNERS");

  return (
    <Portlet id="add-aligners-side-block">
      <PortletTitle as="h2" iconClassName="icon-speech" id="add-aligners-side-block-name">
        <FormattedMessage id="pat.additional.aligners.page.header" />
      </PortletTitle>

      <div className="portlet-body tw-space-y-4">
        {can_order_additional_aligners ? (
          <Button className="tw-inline-block" asChild>
            <NavLink to={`/pages/patient/${patient.patient_id}/additional/aligners`}>
              <FormattedMessage id="pat.status.button.aligners.additional" />
            </NavLink>
          </Button>
        ) : null}

        {showAligners ? (
          <NavLink
            className="tw-inline-block tw-italic"
            to={`/pages/patient/${patient.patient_id}/aligners/additional`}
          >
            <FormattedMessage id="all.additional.aligners" />
          </NavLink>
        ) : null}

        {patient.additional_aligners_patients.length > 0 ? (
          <>
            <h3 className="tw-text-base tw-font-semibold">
              <FormattedMessage id="additional.aligners.patients" />
            </h3>

            <ul className="tw-space-y-1.5 tw-p-0">
              {patient.additional_aligners_patients.map((patientId) => (
                <li key={patientId} className="tw-flex tw-gap-1.5">
                  <span>{patientId}</span>

                  <NavLink
                    to={`/pages/patient/${patientId}`}
                    className="tw-flex tw-w-48 tw-gap-1 tw-truncate"
                  >
                    <PersonName person={patient} />
                  </NavLink>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    </Portlet>
  );
}
